import { pushLayer } from "../history_stack";
import { CHANNELS } from "../../shared/channels";
import { APP_SETTINGS } from "../../shared/app_settings";
import { chiCaption, chiWrote } from "../../shared/chi_text";
import { LogEntry, RefPane, RefPrefs, ReferenceEntry, ReferenceKind } from "../../shared/types";
import { call, listen, mediaSrc } from "../bridge";
import { byId, cloneTemplate, fillFields, flashToast } from "../dom";
import { isViewActive, onViewHidden, onViewShown, showView } from "../navigation";
import { buzz } from "../tweens";
import { debounce, formatDate, matchesSearch } from "../utils";
import { loadRefs, onRefs, refsNow } from "../refs_store";
import { cachedSettings, rememberSettings } from "../settings_cache";
import { UserSettings } from "../../shared/types";
import { locale, t } from "../i18n";
import { isRefFavorite, loadRefFavorites, onRefFavoritesChanged, paintStar, toggleRefFavorite } from "../ref_favorites";
import { cleanPrefs, columnsFor, defaultPrefs, gapFor, KINDS, DESCRIBED, LAYOUTS, learnRatio, masonryColumns, ratioKnown, ratioOf, rowHeightFor, SIZES, SORTS } from "../ref_layout";

// the ref board. tiles are kept per id so a tagging update only touches the tiles that changed

let selected: ReferenceEntry | null = null;
let prefs: RefPrefs = migratedPrefs();
let sheetOpen = "";
let refMode = "scrap";
let indexing = false;
let working: string[] = [];
let trail: string[] = [];
let scale = 1;
let panX = 0;
let panY = 0;
let dragging = false;
let dragged = false;
let lastX = 0;
let lastY = 0;
const tiles = new Map<string, { element: HTMLElement; sig: string }>();
const pickedTags = new Set<string>();
let tapTimer = 0;

// the feed: one column of posts that grows a page at a time as you near the bottom
const FEED_PAGE = 12;
const GROUPED = SORTS.filter((item) => item.grouped).map((item) => item.id);
let feedLimit = FEED_PAGE;
// random keeps one roll per ref for as long as the feed is open, so posts never jump while you scroll
const feedRank = new Map<string, number>();
const posts = new Map<string, { element: HTMLElement; sig: string }>();
const feedHeads = new Map<string, HTMLElement>();
const serials = new WeakMap<HTMLElement, number>();
const planned = new Map<HTMLElement, string>();
let nextSerial = 1;
let relayoutTimer = 0;
let prefsTimer = 0;
const ratios = new Map<string, string>();

function readPref(key: string, fallback: string): string {
  try {
    return localStorage.getItem(key) || fallback;
  } catch {
    return fallback;
  }
}

function writePref(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // private mode
  }
}

// what someone had before the deck existed: the tile size and the stills filter lived on this device, the feed sort in settings
function migratedPrefs(feedSort = ""): RefPrefs {
  const base = defaultPrefs();
  const size = readPref("ref-size", "m");
  if (SIZES.includes(size)) base.board.size = size;
  if (readPref("ref-filter", "all") === "stills") base.kind = "stills";
  if (SORTS.some((item) => item.id === feedSort)) base.feed.sort = feedSort;
  return base;
}

function pane(): RefPane {
  return refMode === "feed" ? prefs.feed : prefs.board;
}

// choices are saved to this person's own settings a moment after the last tap
function savePrefs(): void {
  window.clearTimeout(prefsTimer);
  prefsTimer = window.setTimeout(() => {
    void call<UserSettings>(CHANNELS.settingsSave, { refPrefs: prefs }).then(rememberSettings).catch(() => undefined);
  }, 350);
}

const viewport = () => byId("reference-viewport");
const picture = () => byId<HTMLImageElement>("reference-image");
const clip = () => byId<HTMLVideoElement>("reference-video");
const detail = () => byId("reference-detail");
const references = () => refsNow();

function kindOf(entry: ReferenceEntry): ReferenceKind {
  if (entry.kind) return entry.kind;
  const ext = (entry.fileName || "").split(".").pop()?.toLowerCase() || "";
  if (["mp4", "webm", "mov", "m4v"].includes(ext)) return "video";
  if (ext === "gif" || ext === "apng") return "gif";
  return "image";
}

function isSaved(entry: ReferenceEntry): boolean {
  return !entry.status || entry.status === "saved";
}

function matchesFilter(entry: ReferenceEntry): boolean {
  const kind = kindOf(entry);
  if (prefs.kind === "stills" && kind !== "image") return false;
  if (prefs.kind === "gifs" && kind !== "gif") return false;
  if (prefs.kind === "clips" && kind !== "video") return false;
  if (prefs.favorites && !isRefFavorite(entry.id)) return false;
  const described = Boolean(chiCaption(entry).trim());
  if (prefs.described === "yes" && !described) return false;
  if (prefs.described === "no" && described) return false;
  return true;
}

function paintZoom(): void {
  picture().style.transform = scale === 1 && !panX && !panY ? "" : `translate(${panX}px, ${panY}px) scale(${scale})`;
  viewport().classList.toggle("zoomed", scale > 1.02);
}

function setScale(next: number): void {
  scale = Math.min(8, Math.max(1, next));
  if (scale === 1) {
    panX = 0;
    panY = 0;
  }
  paintZoom();
}

function resetZoom(): void {
  dragging = false;
  viewport().classList.remove("panning");
  setScale(1);
}

function stopClip(): void {
  const video = clip();
  video.pause();
  video.removeAttribute("src");
  video.load();
}

function needsChi(entry: ReferenceEntry): boolean {
  if (kindOf(entry) === "video" || !isSaved(entry) || entry.indexSkipped) return false;
  if (!chiWrote(entry)) return true;
  return (entry.indexSchema || 0) < APP_SETTINGS.refTagSchema;
}

function tileSig(entry: ReferenceEntry): string {
  return [entry.status, entry.error, entry.thumbUrl, entry.fileUrl, entry.caption, entry.author, needsChi(entry), working.includes(entry.id), isRefFavorite(entry.id)].join("|");
}

// a 44pt star in the corner of a tile or post, tapping it never opens the ref
function starButton(entry: ReferenceEntry): HTMLButtonElement {
  const button = document.createElement("button");
  button.type = "button";
  button.className = "ref-star";
  button.dataset.star = entry.id;
  button.setAttribute("aria-label", t("favorite"));
  paintStar(button, entry.id);
  return button;
}

function flipStar(id: string): void {
  toggleRefFavorite(id);
  for (const button of document.querySelectorAll<HTMLElement>("[data-star]")) if (button.dataset.star === id) paintStar(button, id);
  if (prefs.favorites) render();
}

function buildTile(entry: ReferenceEntry): HTMLElement {
  const status = entry.status || "saved";
  const kind = kindOf(entry);
  const tile = fillFields(cloneTemplate("template-reference"), {
    kind: status === "saved" && kind !== "image" ? kind : "",
    queue: status === "failed" ? (entry.error || t("failed")) : status === "downloading" ? t("saving") : status === "queued" ? t("waiting") : "",
    seen: status === "saved" ? chiCaption(entry) : ""
  });
  tile.dataset.id = entry.id;
  tile.classList.toggle("queued", status === "queued");
  tile.classList.toggle("downloading", status === "downloading");
  tile.classList.toggle("failed", status === "failed");
  tile.classList.toggle("working", working.includes(entry.id));
  const stage = tile.querySelector(".scrap-media");
  const overlay = tile.querySelector<HTMLElement>(".scrap-queue");
  if (overlay) overlay.hidden = status === "saved";
  if (status === "saved") {
    const image = document.createElement("img");
    image.alt = "";
    image.loading = "lazy";
    image.decoding = "async";
    image.addEventListener("load", () => {
      image.classList.add("loaded");
      if (learnRatio(entry.id, image.naturalWidth, image.naturalHeight)) scheduleRelayout();
    }, { once: true });
    image.src = mediaSrc(entry.thumbUrl || entry.fileUrl);
    stage?.prepend(image);
    stage?.append(starButton(entry));
    const indexBtn = tile.querySelector<HTMLButtonElement>(".scrap-index");
    if (indexBtn) indexBtn.hidden = !needsChi(entry);
  }
  tile.title = status === "failed" ? (entry.error || t("failed")) : [entry.author, chiCaption(entry)].filter(Boolean).join(" · ");
  return tile;
}

function tileFor(entry: ReferenceEntry): HTMLElement {
  const sig = tileSig(entry);
  const cached = tiles.get(entry.id);
  if (cached && cached.sig === sig) return cached.element;
  const element = buildTile(entry);
  tiles.set(entry.id, { element, sig });
  return element;
}

function chiBlurb(entry: ReferenceEntry): string {
  return [chiCaption(entry), (entry.tags || []).join(" ")].filter(Boolean).join(" ");
}

function undocumented(): ReferenceEntry[] {
  return references().filter((entry) => isSaved(entry) && !String(entry.note || "").trim());
}

function visibleEntries(): ReferenceEntry[] {
  const search = byId<HTMLInputElement>("references-search").value;
  const pool = refMode === "manual" ? undocumented() : references();
  return pool.filter((entry) => {
    if (!isSaved(entry)) return refMode !== "manual" && matchesSearch(search, entry.author, entry.text, entry.error || "", entry.status || "");
    if (refMode !== "manual" && !matchesFilter(entry)) return false;
    if (pickedTags.size && ![...pickedTags].every((tag) => (entry.tags || []).includes(tag))) return false;
    return matchesSearch(search, entry.author, entry.text, entry.note || "", chiBlurb(entry), kindOf(entry));
  });
}

function savedPool(): ReferenceEntry[] {
  const pool = visibleEntries().filter(isSaved);
  return orderRefs(pool).list;
}

function addedTime(entry: ReferenceEntry): number {
  return Date.parse(entry.savedAt || "") || 0;
}

function feedTags(entry: ReferenceEntry): string[] {
  return [...new Set((entry.tags || []).filter((tag) => !tag.startsWith("rating:") && tag !== "dropped"))];
}

function rankOf(id: string): number {
  let rank = feedRank.get(id);
  if (rank === undefined) {
    rank = Math.random();
    feedRank.set(id, rank);
  }
  return rank;
}

interface Ordered {
  list: ReferenceEntry[];
  groupOf: Map<string, string>;
  groupSize: Map<string, number>;
}

const TYPE_ORDER = ["stills", "gifs", "clips"];

// a group key per ref for the grouped sorts. tags puts each ref under its most used tag, leaving out the ones you picked
function groupKeyOf(entry: ReferenceEntry, sort: string, counts: Map<string, number>): string {
  if (sort === "author") return String(entry.author || "").trim();
  if (sort === "month") return String(entry.savedAt || "").slice(0, 7);
  if (sort === "type") {
    const kind = kindOf(entry);
    return kind === "video" ? "clips" : kind === "gif" ? "gifs" : "stills";
  }
  let best = "";
  for (const tag of feedTags(entry)) {
    if (pickedTags.has(tag)) continue;
    const count = counts.get(tag) || 0;
    const top = counts.get(best) || 0;
    if (!best || count > top || (count === top && tag < best)) best = tag;
  }
  return best;
}

function groupLabel(sort: string, key: string): string {
  if (sort === "month") {
    const [year, month] = key.split("-").map(Number);
    return year && month ? new Date(year, month - 1, 1).toLocaleDateString(locale(), { month: "long", year: "numeric" }) : t("unknown date");
  }
  if (sort === "type") return t(key);
  if (sort === "author") return key || t("unlabeled");
  return key ? key.replace(/_/g, " ") : t("untagged");
}

function orderRefs(pool: ReferenceEntry[]): Ordered {
  const { sort, reverse } = pane();
  const groupOf = new Map<string, string>();
  const groupSize = new Map<string, number>();
  const newest = (a: ReferenceEntry, b: ReferenceEntry) => addedTime(b) - addedTime(a);
  const flat = (list: ReferenceEntry[]): Ordered => ({ list: reverse ? list.reverse() : list, groupOf, groupSize });
  if (sort === "new") return flat([...pool].sort(newest));
  if (sort === "old") return flat([...pool].sort((a, b) => addedTime(a) - addedTime(b)));
  if (sort === "most") return flat([...pool].sort((a, b) => feedTags(b).length - feedTags(a).length || newest(a, b)));
  if (sort === "undescribed") return flat([...pool].sort((a, b) => Number(Boolean(chiCaption(a).trim())) - Number(Boolean(chiCaption(b).trim())) || newest(a, b)));
  if (!GROUPED.includes(sort)) return { list: [...pool].sort((a, b) => rankOf(a.id) - rankOf(b.id)), groupOf, groupSize };
  const counts = new Map<string, number>();
  if (sort === "tags") for (const entry of pool) for (const tag of feedTags(entry)) if (!pickedTags.has(tag)) counts.set(tag, (counts.get(tag) || 0) + 1);
  for (const entry of pool) {
    const key = groupKeyOf(entry, sort, counts);
    groupOf.set(entry.id, key);
    groupSize.set(key, (groupSize.get(key) || 0) + 1);
  }
  const keys = [...groupSize.keys()];
  // an empty key (no tag, no author, no date) always goes last, flipping the order puts it first
  const named = (a: string, b: string) => (!a || !b ? (a ? -1 : b ? 1 : 0) : 0);
  keys.sort((a, b) => {
    if (named(a, b)) return named(a, b);
    if (sort === "month") return b.localeCompare(a);
    if (sort === "author") return a.localeCompare(b, locale(), { sensitivity: "base" });
    if (sort === "type") return TYPE_ORDER.indexOf(a) - TYPE_ORDER.indexOf(b);
    return (groupSize.get(b) || 0) - (groupSize.get(a) || 0) || a.localeCompare(b);
  });
  if (reverse) keys.reverse();
  const place = new Map(keys.map((key, at) => [key, at]));
  const list = [...pool].sort((a, b) => (place.get(groupOf.get(a.id) || "") || 0) - (place.get(groupOf.get(b.id) || "") || 0) || newest(a, b));
  return { list, groupOf, groupSize };
}

function addedAgo(entry: ReferenceEntry): string {
  const time = addedTime(entry);
  if (!time) return "";
  const days = Math.floor((Date.now() - time) / 86400000);
  if (days < 1) return t("today");
  if (days < 2) return t("yesterday");
  if (days < 7) return t("{days}d", { days });
  if (days < 56) return t("{weeks}w", { weeks: Math.floor(days / 7) });
  return formatDate(entry.savedAt);
}

function postSig(entry: ReferenceEntry): string {
  return [entry.fileUrl, entry.thumbUrl, entry.author, entry.text, entry.note, entry.caption, feedTags(entry).join(","), kindOf(entry), entry.savedAt, isRefFavorite(entry.id)].join("|");
}

function buildPost(entry: ReferenceEntry): HTMLElement {
  const kind = kindOf(entry);
  const post = document.createElement("article");
  post.className = "feed-post";
  post.dataset.id = entry.id;

  const head = document.createElement("header");
  head.className = "feed-head";
  const who = (entry.author || "").replace(/^@/, "");
  const avatar = document.createElement("span");
  avatar.className = "feed-avatar";
  avatar.textContent = (who[0] || "r").toUpperCase();
  const name = document.createElement("b");
  name.className = "feed-name";
  name.textContent = entry.author || t("unlabeled");
  const meta = document.createElement("small");
  meta.className = "feed-meta";
  meta.textContent = [addedAgo(entry), kind === "image" ? "" : kind === "video" ? t("clip") : "gif"].filter(Boolean).join(" · ");
  head.append(avatar, name, meta);
  post.append(head);

  const words = String(entry.note || "").trim() || entry.text || chiCaption(entry);
  if (words) {
    const text = document.createElement("p");
    text.className = "feed-text";
    text.textContent = words;
    post.append(text);
  }

  const media = document.createElement("div");
  media.className = "feed-media";
  const ratio = ratios.get(entry.id) || (ratioKnown(entry.id) ? `${ratioOf(entry.id)} / 1` : "");
  if (ratio) media.style.aspectRatio = ratio;
  const remember = (width: number, height: number) => {
    if (!width || !height) return;
    const value = `${width} / ${height}`;
    ratios.set(entry.id, value);
    media.style.aspectRatio = value;
    media.classList.add("loaded");
    if (learnRatio(entry.id, width, height)) scheduleRelayout();
  };
  if (kind === "video") {
    const video = document.createElement("video");
    video.muted = true;
    video.loop = true;
    video.playsInline = true;
    video.preload = "metadata";
    if (entry.thumbUrl) video.poster = mediaSrc(entry.thumbUrl);
    video.addEventListener("loadedmetadata", () => remember(video.videoWidth, video.videoHeight), { once: true });
    // ios only paints a first frame when asked for a time
    video.src = `${mediaSrc(entry.fileUrl)}#t=0.1`;
    media.append(video);
    const badge = document.createElement("span");
    badge.className = "scrap-kind";
    badge.textContent = t("clip");
    media.append(badge);
  } else {
    const image = document.createElement("img");
    image.alt = "";
    image.loading = "lazy";
    image.decoding = "async";
    image.addEventListener("load", () => remember(image.naturalWidth, image.naturalHeight), { once: true });
    // gifs and stills at full size, the 400px thumbs look soft across a phone
    image.src = mediaSrc(entry.fileUrl || entry.thumbUrl || "");
    media.append(image);
  }
  media.append(starButton(entry));
  post.append(media);

  const tags = feedTags(entry).slice(0, 12);
  if (tags.length) {
    const row = document.createElement("div");
    row.className = "feed-tags";
    row.append(...tags.map((tag) => {
      const chip = document.createElement("button");
      chip.type = "button";
      chip.className = "feed-tag";
      chip.dataset.tag = tag;
      chip.textContent = `#${tag.replace(/_/g, " ")}`;
      return chip;
    }));
    post.append(row);
  }
  return post;
}

function postFor(entry: ReferenceEntry): HTMLElement {
  const sig = postSig(entry);
  const cached = posts.get(entry.id);
  if (cached && cached.sig === sig) return cached.element;
  const element = buildPost(entry);
  posts.set(entry.id, { element, sig });
  return element;
}

function headFor(sort: string, key: string, size: number): HTMLElement {
  const id = `${sort}|${key}|${size}|${locale()}`;
  let head = feedHeads.get(id);
  if (!head) {
    head = document.createElement("h2");
    head.className = "feed-group";
    const label = document.createElement("span");
    label.textContent = groupLabel(sort, key);
    const count = document.createElement("small");
    count.textContent = String(size);
    head.append(label, count);
    feedHeads.set(id, head);
  }
  return head;
}

function serial(element: HTMLElement): number {
  let id = serials.get(element);
  if (!id) {
    id = nextSerial++;
    serials.set(element, id);
  }
  return id;
}

// ratios of rows and masonry come from the pictures themselves, so those two settle a moment after the tiles load
function scheduleRelayout(): void {
  window.clearTimeout(relayoutTimer);
  relayoutTimer = window.setTimeout(() => {
    const layout = pane().layout;
    if (layout === "rows") restyleRows();
    else if (layout === "masonry") render();
  }, 220);
}

function styleRow(element: HTMLElement, height: number): void {
  const ratio = ratioOf(element.dataset.id || "");
  element.style.setProperty("--r", String(ratio));
  element.style.setProperty("--g", String(Math.round(ratio * 100)));
  element.style.setProperty("--h", `${height}px`);
}

function restyleRows(): void {
  const host = byId(refMode === "feed" ? "references-feed" : "references-grid");
  const height = rowHeightFor(pane().size, host.clientWidth);
  host.querySelectorAll<HTMLElement>(".ref-block > [data-id]").forEach((element) => styleRow(element, height));
}

// lays the tiles (board) or posts (feed) out in the chosen layout. consecutive refs of one group sit in one block under their heading
function arrange(host: HTMLElement, feed: boolean, list: ReferenceEntry[], ordered: Ordered, front: ReferenceEntry[]): void {
  const { layout, size, sort } = pane();
  const width = host.clientWidth || host.parentElement?.clientWidth || window.innerWidth;
  const count = layout === "posts" ? 1 : columnsFor(layout, size, width);
  const height = rowHeightFor(size, width);
  const make = (entry: ReferenceEntry) => feed ? postFor(entry) : tileFor(entry);
  const blocks: Array<{ head: HTMLElement | null; entries: ReferenceEntry[] }> = [];
  for (const entry of list) {
    if (GROUPED.includes(sort)) {
      const key = ordered.groupOf.get(entry.id) || "";
      const last = blocks[blocks.length - 1];
      if (!last || last.head?.dataset.key !== key) {
        const head = headFor(sort, key, ordered.groupSize.get(key) || 0);
        head.dataset.key = key;
        blocks.push({ head, entries: [] });
      }
    } else if (!blocks.length) {
      blocks.push({ head: null, entries: [] });
    }
    blocks[blocks.length - 1].entries.push(entry);
  }
  if (front.length) {
    if (!blocks.length) blocks.push({ head: null, entries: [] });
    blocks[0].entries.unshift(...front);
  }
  const plan = blocks.map((block) => ({
    ...block,
    columns: layout === "masonry" ? masonryColumns(block.entries.map((entry) => entry.id), count, feed ? 0.55 : 0) : null
  }));
  const sig = [layout, size, count, height, ...plan.map((block) => `${block.head ? serial(block.head) : "-"}:${block.columns ? block.columns.map((column) => column.join(",")).join("|") : block.entries.map((entry) => serial(make(entry))).join(",")}`)].join(";");
  host.dataset.layout = layout;
  host.dataset.size = size;
  if (planned.get(host) === sig) return;
  planned.set(host, sig);
  const nodes: HTMLElement[] = [];
  for (const block of plan) {
    if (block.head) nodes.push(block.head);
    const holder = document.createElement("div");
    holder.className = "ref-block";
    holder.style.setProperty("--gap", `${gapFor(layout)}px`);
    holder.style.setProperty("--n", String(count));
    const made = new Map(block.entries.map((entry) => [entry.id, make(entry)]));
    if (block.columns) {
      holder.append(...block.columns.map((ids) => {
        const column = document.createElement("div");
        column.className = "ref-col";
        column.append(...ids.map((id) => made.get(id)!));
        return column;
      }));
    } else {
      holder.append(...made.values());
    }
    nodes.push(holder);
  }
  host.replaceChildren(...nodes);
  for (const [id, element] of tiles) if (!element.element.isConnected && !references().some((entry) => entry.id === id)) tiles.delete(id);
  if (layout === "rows" || layout === "masonry") host.querySelectorAll<HTMLElement>(".ref-block [data-id]").forEach((element) => {
    if (layout === "rows") styleRow(element, height);
    else element.style.setProperty("--r", String(ratioOf(element.dataset.id || "")));
  });
}

// another page once you're within a few screens of the end
function feedMaybeMore(): void {
  if (refMode !== "feed" || !isViewActive("references")) return;
  const view = byId("view-references");
  const total = visibleEntries().filter(isSaved).length;
  if (feedLimit >= total) return;
  if (view.scrollTop + view.clientHeight < view.scrollHeight - view.clientHeight * 2.5) return;
  feedLimit += FEED_PAGE;
  render();
}

function resetFeed(): void {
  feedLimit = FEED_PAGE;
  if (refMode === "feed") byId("view-references").scrollTop = 0;
}

// a change on the deck: remembered for this person and painted straight away
function setPane(change: Partial<RefPane>): void {
  Object.assign(pane(), change);
  resetFeed();
  savePrefs();
  render();
}

function toggleSheet(name: string): void {
  sheetOpen = sheetOpen === name ? "" : name;
  paintSheet();
}

function pickSort(sort: string): void {
  if (!SORTS.some((item) => item.id === sort)) return;
  if (sort === "random") {
    if (pane().sort === "random") flashToast(t("shuffled"));
    feedRank.clear();
  }
  setPane({ sort, reverse: false });
}

function setFilter(change: Partial<Pick<RefPrefs, "kind" | "described" | "favorites">>): void {
  Object.assign(prefs, change);
  resetFeed();
  savePrefs();
  render();
}

function glyphSvg(glyph: string): string {
  return `<svg viewBox="0 0 38 28" width="22" height="16" fill="currentColor" aria-hidden="true">${glyph}</svg>`;
}

function deckKey(label: string, attribute: string, value: string, active: boolean, extra?: HTMLElement): HTMLButtonElement {
  const button = document.createElement("button");
  button.type = "button";
  button.dataset[attribute] = value;
  button.setAttribute("aria-pressed", String(active));
  button.textContent = label;
  if (extra) button.append(extra);
  return button;
}

function sheetHeading(text: string): HTMLElement {
  const heading = document.createElement("h2");
  heading.className = "deck-heading";
  heading.textContent = text;
  return heading;
}

function segment(items: Array<[string, string]>, attribute: string, current: string): HTMLElement {
  const row = document.createElement("div");
  row.className = "deck-seg";
  row.append(...items.map(([value, label]) => deckKey(label, attribute, value, value === current)));
  return row;
}

// the popover under the deck: layouts and tile size, the sorts, or the filters
function paintSheet(): void {
  const sheet = byId("references-sheet");
  for (const [id, name] of [["references-layout", "layout"], ["references-sort", "sort"], ["references-filter", "filter"]]) {
    byId(id).setAttribute("aria-expanded", String(sheetOpen === name));
  }
  sheet.hidden = !sheetOpen;
  if (!sheetOpen) {
    sheet.replaceChildren();
    return;
  }
  const p = pane();
  const parts: HTMLElement[] = [];
  if (sheetOpen === "layout") {
    const grid = document.createElement("div");
    grid.className = "deck-layouts";
    grid.append(...LAYOUTS.filter((item) => !item.feedOnly || refMode === "feed").map((item) => {
      const glyph = document.createElement("span");
      glyph.innerHTML = glyphSvg(item.glyph);
      const button = deckKey(t(item.label), "layout", item.id, item.id === p.layout, glyph);
      button.prepend(glyph);
      return button;
    }));
    parts.push(sheetHeading(t("layout")), grid);
    if (p.layout !== "posts") parts.push(sheetHeading(t("tile size")), segment([["s", t("small")], ["m", t("medium")], ["l", t("large")]], "size", p.size));
  } else if (sheetOpen === "sort") {
    const list = document.createElement("div");
    list.className = "deck-sorts";
    list.append(...SORTS.map((item) => {
      const tag = document.createElement("small");
      tag.textContent = item.grouped ? t("groups") : "";
      return deckKey(t(item.label), "sort", item.id, item.id === p.sort, tag);
    }));
    const hint = document.createElement("p");
    hint.className = "hint";
    const current = SORTS.find((item) => item.id === p.sort);
    hint.textContent = current ? `${t(current.hint)}${p.reverse && p.sort !== "random" ? `, ${t("reversed")}` : ""}` : "";
    parts.push(sheetHeading(t("sort by")), list, hint);
  } else {
    parts.push(
      sheetHeading(t("show")), segment(KINDS.map((kind) => [kind, t(kind === "all" ? "all" : kind)] as [string, string]), "kind", prefs.kind),
      sheetHeading(t("description")), segment(DESCRIBED.map((value) => [value, t(value === "all" ? "any" : value === "yes" ? "described" : "not yet")] as [string, string]), "described", prefs.described),
      sheetHeading(t("favorites")), segment([["all", t("all")], ["favorites", `★ ${t("favorites")}`]], "favorites", prefs.favorites ? "favorites" : "all")
    );
  }
  sheet.replaceChildren(...parts);
  const deck = byId("references-deck");
  sheet.style.top = `${deck.offsetTop + deck.offsetHeight + 6}px`;
}

function paintDeck(): void {
  const p = pane();
  const layout = LAYOUTS.find((item) => item.id === p.layout) || LAYOUTS[0];
  byId("references-layout-glyph").innerHTML = glyphSvg(layout.glyph);
  byId("references-layout-name").textContent = t(layout.label);
  byId("references-sort-name").textContent = t(SORTS.find((item) => item.id === p.sort)?.label || "Newest");
  const reverse = byId<HTMLButtonElement>("references-reverse");
  reverse.hidden = p.sort === "random";
  reverse.classList.toggle("flipped", p.reverse);
  reverse.setAttribute("aria-pressed", String(p.reverse));
  byId("references-filter-dot").hidden = prefs.kind === "all" && prefs.described === "all" && !prefs.favorites;
  paintSheet();
}

function showRandom(): void {
  const pool = savedPool();
  if (!pool.length) return flashToast(t("nothing to show"));
  const others = selected ? pool.filter((entry) => entry.id !== selected?.id) : pool;
  const choices = others.length ? others : pool;
  openDetail(choices[Math.floor(Math.random() * choices.length)]);
}

function showStep(step: number): void {
  const pool = savedPool();
  if (!pool.length) return flashToast(t("nothing to show"));
  const index = selected ? pool.findIndex((entry) => entry.id === selected?.id) : -1;
  openDetail(pool[(index + step + pool.length) % pool.length]);
}

function showBack(): void {
  trail.pop();
  const previous = trail.pop();
  const entry = previous ? references().find((item) => item.id === previous) : null;
  if (entry) openDetail(entry);
  else showStep(-1);
}

async function askChi(id: string, redo: boolean): Promise<void> {
  try {
    await call(redo ? CHANNELS.referencesReindex : CHANNELS.referencesIndexOne, id);
    flashToast(redo ? t("chi is looking again") : t("chi is looking at this one next"));
  } catch (error) {
    flashToast(error instanceof Error ? error.message : t("could not index that ref"));
  }
}

function paintViewerTags(entry: ReferenceEntry): void {
  byId("reference-tag-chips").replaceChildren(...(entry.tags || []).filter((tag) => !tag.startsWith("rating:")).map((tag) => {
    const chip = document.createElement("button");
    chip.type = "button";
    chip.className = "chip";
    chip.dataset.tag = tag;
    chip.textContent = tag.replace(/_/g, " ");
    return chip;
  }));
}

function paintChi(entry: ReferenceEntry): void {
  const text = chiCaption(entry);
  byId("reference-peek-text").textContent = text || entry.note || entry.author || t("details");
  const seen = byId("reference-seen");
  seen.hidden = !text;
  seen.textContent = text;
  const video = kindOf(entry) === "video";
  const box = byId<HTMLTextAreaElement>("reference-caption");
  if (document.activeElement !== box) box.value = text;
  box.placeholder = video ? t("clips are not described, write your own") : entry.indexSkipped ? t("chi left this one ({reason}), write your own", { reason: entry.indexSkipped }) : working.includes(entry.id) ? t("chi is looking at this…") : t("not described yet, write your own or tap spark");
}

async function saveCaption(): Promise<void> {
  if (!selected) return;
  const box = byId<HTMLTextAreaElement>("reference-caption");
  const next = box.value.replace(/\s+/g, " ").trim();
  if (next === chiCaption(selected).trim()) return;
  try {
    selected = await call<ReferenceEntry>(CHANNELS.referencesCaption, selected.id, next);
    byId("reference-caption-state").textContent = t("saved");
    window.setTimeout(() => (byId("reference-caption-state").textContent = ""), 1600);
  } catch (error) {
    flashToast(error instanceof Error ? error.message : t("could not save the description"));
  }
}

function paintIndexMeter(): void {
  const entry = selected;
  const label = byId("reference-index-label");
  const fill = byId("reference-index-fill");
  const track = fill.parentElement;
  if (!entry || !track) return;
  track.classList.remove("scanning", "done");
  fill.style.removeProperty("width");
  if (kindOf(entry) === "video") {
    label.textContent = t("chi skips clips");
    return;
  }
  if (chiWrote(entry)) {
    track.classList.add("done");
    label.textContent = t("described");
    return;
  }
  if (working.includes(entry.id)) {
    track.classList.add("scanning");
    label.textContent = t("chi is looking");
    return;
  }
  label.textContent = entry.indexSkipped ? t("skipped") : t("waiting on chi");
}

// the tags you use most, or the ones matching what you typed, as one tap filters
function paintTagChips(pool: ReferenceEntry[]): void {
  const search = byId<HTMLInputElement>("references-search").value.trim().toLowerCase().replace(/\s+/g, "_");
  const counts = new Map<string, number>();
  for (const entry of pool) {
    for (const tag of entry.tags || []) {
      if (tag.startsWith("rating:") || tag === "dropped" || pickedTags.has(tag)) continue;
      counts.set(tag, (counts.get(tag) || 0) + 1);
    }
  }
  let ranked = [...counts.entries()].sort((a, b) => b[1] - a[1]);
  if (search) ranked = ranked.filter(([tag]) => tag.includes(search));
  const picked: Array<[string, number]> = [...pickedTags].map((tag) => [tag, 0]);
  const chips = [...picked, ...ranked.slice(0, 40)].map(([tag, count]) => {
    const chip = document.createElement("button");
    chip.type = "button";
    chip.className = "chip tag-filter";
    chip.dataset.tag = tag;
    chip.classList.toggle("active", pickedTags.has(tag));
    const label = document.createElement("span");
    label.textContent = tag.replace(/_/g, " ");
    chip.append(label);
    if (count) {
      const small = document.createElement("small");
      small.textContent = String(count);
      chip.append(small);
    }
    return chip;
  });
  byId("references-tags").replaceChildren(...chips);
}

function paintIndexStrip(all: ReferenceEntry[]): void {
  const stills = all.filter((entry) => isSaved(entry) && kindOf(entry) !== "video");
  const left = stills.filter(needsChi).length;
  const done = stills.length - left;
  byId("references-index-text").textContent = stills.length
    ? left ? t(indexing ? "tagging {done}/{total} · {left} left" : "tagged {done}/{total} · {left} left", { done, total: stills.length, left }) : t("all {total} tagged", { total: stills.length })
    : "";
  const bar = byId("references-index-bar");
  bar.style.width = stills.length ? `calc(${((done / stills.length) * 100).toFixed(1)}% - 4px)` : "0";
  bar.parentElement?.classList.toggle("live", indexing);
}

function paintCounter(): void {
  if (!selected) return;
  const pool = savedPool();
  const at = pool.findIndex((entry) => entry.id === selected?.id);
  byId("reference-counter").textContent = at >= 0 ? `${at + 1}/${pool.length}` : "";
}

function render(): void {
  if (!isViewActive("references") && detail().hidden) return;
  const feed = refMode === "feed";
  const visible = feed ? visibleEntries().filter(isSaved) : visibleEntries();
  const ordered = orderRefs(visible.filter(isSaved));
  if (feed) {
    const shown = ordered.list.slice(0, feedLimit);
    arrange(byId("references-feed"), true, shown, ordered, []);
    const more = byId("references-feed-more");
    more.textContent = !ordered.list.length ? "" : shown.length < ordered.list.length ? t("loading more") : t("that's all {count}", { count: ordered.list.length });
    requestAnimationFrame(feedMaybeMore);
  } else {
    arrange(byId("references-grid"), false, ordered.list, ordered, visible.filter((entry) => !isSaved(entry)));
  }
  const live = new Set(references().map((entry) => entry.id));
  for (const id of tiles.keys()) if (!live.has(id)) tiles.delete(id);
  for (const id of posts.keys()) if (!live.has(id)) posts.delete(id);
  if (feedHeads.size > 400) feedHeads.clear();

  const all = references();
  const waiting = all.filter((entry) => entry.status === "queued" || entry.status === "downloading").length;
  const failed = all.filter((entry) => entry.status === "failed").length;
  const saved = all.filter(isSaved).length;
  const pending = all.filter(needsChi).length;
  const bits = [visible.length === all.length ? `${saved}` : t("{shown} of {total}", { shown: visible.length, total: all.length })];
  if (waiting) bits.push(t("{count} adding", { count: waiting }));
  if (failed) bits.push(t("{count} failed", { count: failed }));
  byId("references-count").textContent = refMode === "manual" ? t("{count} to describe", { count: visible.length }) : bits.join(" · ");
  paintIndexStrip(all);
  paintTagChips(refMode === "manual" ? visible : all.filter(isSaved));

  paintIndexMeter();
  const indexBtn = byId<HTMLButtonElement>("references-index");
  indexBtn.hidden = refMode === "manual";
  indexBtn.disabled = pending === 0 || indexing;
  indexBtn.classList.toggle("tween-pulse", indexing);
  indexBtn.querySelector("span")!.textContent = indexing ? t("busy") : t("tag");
  byId("references-filter").hidden = refMode === "manual";
  byId("references-random").hidden = refMode === "manual";
  paintDeck();
  const empty = byId("references-empty");
  empty.hidden = visible.length > 0;
  empty.textContent = all.length === 0
    ? t("tap + to add pictures, drop files here, or use + ref in chrome")
    : refMode === "manual" ? t("every ref already has your note") : t("nothing matches");
}

function refreshSelected(): void {
  if (!selected) return;
  const fresh = references().find((entry) => entry.id === selected?.id);
  if (!fresh) return;
  selected = fresh;
  if (detail().hidden) return;
  paintChi(fresh);
  paintIndexMeter();
  const tags = byId<HTMLInputElement>("reference-tags");
  if (document.activeElement !== tags) tags.value = (fresh.tags || []).join(", ");
}

async function reload(): Promise<void> {
  try {
    await loadRefs(true);
  } catch (error) {
    flashToast(error instanceof Error ? error.message : t("could not load references"));
  }
}

function setInfo(on: boolean): void {
  detail().classList.toggle("show-info", on);
  byId("reference-info").classList.toggle("active", on);
  writePref("ref-info", on ? "1" : "0");
}

function openDetail(entry: ReferenceEntry, focusNote = false): void {
  selected = entry;
  if (trail[trail.length - 1] !== entry.id) trail = [...trail.slice(-40), entry.id];
  resetZoom();
  const video = kindOf(entry) === "video";
  picture().hidden = video;
  clip().hidden = !video;
  if (video) {
    picture().removeAttribute("src");
    clip().src = mediaSrc(entry.fileUrl);
    void clip().play().catch(() => undefined);
  } else {
    stopClip();
    picture().src = mediaSrc(entry.fileUrl);
  }
  const caption = [entry.author, entry.text].filter(Boolean).join(" · ");
  byId("reference-kind").textContent = video ? t("video") : kindOf(entry) === "gif" ? "gif" : "";
  byId("reference-author").textContent = caption || t("unlabeled");
  paintStar(byId("reference-star"), entry.id);
  paintChi(entry);
  paintIndexMeter();
  byId<HTMLTextAreaElement>("reference-note").value = entry.note || "";
  byId<HTMLInputElement>("reference-tags").value = (entry.tags || []).join(", ");
  paintViewerTags(entry);
  paintCounter();
  detail().classList.remove("chrome-hidden");
  byId<HTMLButtonElement>("reference-open").disabled = !entry.tweetUrl;
  byId<HTMLButtonElement>("reference-reindex").hidden = video;
  if (detail().hidden) {
    detail().hidden = false;
    document.body.classList.add("ref-open");
    releaseLayer = pushLayer(() => {
      releaseLayer = null;
      void closeDetail();
    });
    // the side panel stays open on desktop, phones start with just the peek handle
    const desk = matchMedia("(min-width: 900px)").matches;
    setInfo(focusNote || (desk && readPref("ref-info", "1") === "1"));
  }
  if (focusNote) {
    setInfo(true);
    byId<HTMLTextAreaElement>("reference-note").focus();
  }
}

// opens the lightbox over whatever view is showing, so closing it lands back where you were (chat, today)
export async function openReference(id: string): Promise<void> {
  if (!references().some((entry) => entry.id === id)) await reload();
  const entry = references().find((item) => item.id === id);
  if (entry && isSaved(entry)) openDetail(entry);
}

async function saveCurrent(): Promise<void> {
  if (!selected) return;
  const tags = byId<HTMLInputElement>("reference-tags").value.split(",");
  const note = byId<HTMLTextAreaElement>("reference-note").value;
  const changed = note !== (selected.note || "") || tags.map((tag) => tag.trim()).filter(Boolean).join(",") !== (selected.tags || []).join(",");
  if (changed) await call(CHANNELS.referencesTag, selected.id, tags, note);
}

let releaseLayer: (() => void) | null = null;

function hideDetail(): void {
  selected = null;
  trail = [];
  resetZoom();
  stopClip();
  detail().hidden = true;
  document.body.classList.remove("ref-open");
  const release = releaseLayer;
  releaseLayer = null;
  release?.();
}

async function closeDetail(): Promise<void> {
  if (detail().hidden) return;
  await saveCaption();
  await saveCurrent();
  hideDetail();
}

async function saveAndNext(): Promise<void> {
  if (detail().hidden || !selected) return;
  const id = selected.id;
  await saveCurrent();
  await reload();
  const next = savedPool().find((entry) => entry.id !== id);
  if (next) return openDetail(next, refMode === "manual");
  hideDetail();
  if (refMode === "manual") flashToast(t("all described"));
}

function isRefFile(file: File): boolean {
  return file.type.startsWith("image/") || file.type.startsWith("video/") || /\.(gif|webp|apng|mp4|webm|mov|m4v)$/i.test(file.name);
}

function readFile(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(new Error(t("could not read that file")));
    reader.readAsDataURL(file);
  });
}

async function importFiles(files: File[], urls: string[] = []): Promise<void> {
  const usable = files.filter(isRefFile);
  if (!usable.length && !urls.length) return flashToast(t("nothing usable there"));
  flashToast(t("adding {count}", { count: usable.length + urls.length }));
  try {
    for (const file of usable) await call(CHANNELS.referencesImport, await readFile(file), file.name);
    for (const url of urls) await call(CHANNELS.referencesImport, url, url);
  } catch (error) {
    flashToast(error instanceof Error ? error.message : t("could not import that file"));
  }
}

async function importDropped(event: DragEvent): Promise<void> {
  event.preventDefault();
  byId("view-references").classList.remove("drop-hot");
  const files = [...(event.dataTransfer?.files ?? [])];
  const urls = (event.dataTransfer?.getData("text/uri-list") || "").split("\n").map((line) => line.trim()).filter((line) => /^https?:\/\//.test(line));
  await importFiles(files, urls);
}

// pinch and wheel zoom, drag to pan, swipe to flip when not zoomed, swipe down to close
function setupGestures(): void {
  const stage = viewport();
  const image = picture();
  let pinchStart = 0;
  let pinchScale = 1;
  let lastTap = 0;
  let swipeX = 0;
  let swipeY = 0;
  let swiping = false;

  stage.addEventListener("wheel", (event) => {
    if (!clip().hidden) return;
    event.preventDefault();
    setScale(event.deltaY < 0 ? scale * 1.12 : scale / 1.12);
  }, { passive: false });

  stage.addEventListener("touchstart", (event) => {
    if (event.touches.length === 2 && clip().hidden) {
      event.preventDefault();
      swiping = false;
      pinchStart = Math.hypot(event.touches[0].clientX - event.touches[1].clientX, event.touches[0].clientY - event.touches[1].clientY);
      pinchScale = scale;
    } else if (event.touches.length === 1 && scale <= 1) {
      swiping = true;
      swipeX = event.touches[0].clientX;
      swipeY = event.touches[0].clientY;
    }
  }, { passive: false });

  stage.addEventListener("touchmove", (event) => {
    // ios ignores touch-action on some layers, so keep one finger drags from scrolling anything behind
    if (event.touches.length === 1) {
      if (event.target !== clip()) event.preventDefault();
      return;
    }
    if (event.touches.length !== 2 || pinchStart < 8 || !clip().hidden) return;
    event.preventDefault();
    const distance = Math.hypot(event.touches[0].clientX - event.touches[1].clientX, event.touches[0].clientY - event.touches[1].clientY);
    setScale(pinchScale * (distance / pinchStart));
  }, { passive: false });

  stage.addEventListener("touchend", (event) => {
    if (!swiping || event.touches.length) return;
    swiping = false;
    const touch = event.changedTouches[0];
    const dx = touch.clientX - swipeX;
    const dy = touch.clientY - swipeY;
    if (scale > 1) return;
    // ios drops the click when touchmove was prevented, so a tap on the empty dark closes here instead
    const target = event.target as HTMLElement;
    if (Math.hypot(dx, dy) < 10 && (target.id === "reference-stage" || target.id === "reference-viewport")) {
      event.preventDefault();
      void closeDetail();
      return;
    }
    if (Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(dy) * 1.3) {
      buzz();
      if (dx < 0) showStep(1);
      else showBack();
    } else if (dy > 110 && Math.abs(dy) > Math.abs(dx) * 1.5) {
      void closeDetail();
    }
  });

  image.addEventListener("pointerdown", (event) => {
    if (event.button !== 0 || scale <= 1) return;
    dragging = true;
    dragged = false;
    lastX = event.clientX;
    lastY = event.clientY;
    image.setPointerCapture(event.pointerId);
    stage.classList.add("panning");
  });

  image.addEventListener("pointermove", (event) => {
    if (!dragging) return;
    const dx = event.clientX - lastX;
    const dy = event.clientY - lastY;
    if (Math.abs(dx) + Math.abs(dy) > 4) dragged = true;
    lastX = event.clientX;
    lastY = event.clientY;
    panX += dx;
    panY += dy;
    paintZoom();
  });

  let downX = 0;
  let downY = 0;
  image.addEventListener("pointerdown", (event) => {
    downX = event.clientX;
    downY = event.clientY;
  });
  image.addEventListener("pointerup", (event) => {
    dragging = false;
    stage.classList.remove("panning");
    if (dragged || Math.hypot(event.clientX - downX, event.clientY - downY) > 10) return;
    const now = Date.now();
    window.clearTimeout(tapTimer);
    if (event.pointerType === "touch" && now - lastTap < 300) {
      setScale(scale > 1 ? 1 : 2.4);
      lastTap = 0;
      return;
    }
    lastTap = now;
    // one tap clears everything off the picture, another brings it back
    tapTimer = window.setTimeout(() => detail().classList.toggle("chrome-hidden"), event.pointerType === "touch" ? 300 : 0);
  });

  image.addEventListener("dblclick", () => {
    if (scale > 1) resetZoom();
    else setScale(2.4);
  });
}

export async function setupReferencesView(): Promise<void> {
  const view = byId("view-references");
  // the lightbox lives on body: inside the scrolling view ios safari clips a fixed layer to the scroller,
  // scrolls it with the grid and sends taps through to the tiles underneath
  document.body.append(detail());
  onViewHidden("references", () => {
    void closeDetail();
    // coming back to the feed rolls a new order
    feedRank.clear();
    feedLimit = FEED_PAGE;
  });
  view.addEventListener("dragover", (event) => {
    event.preventDefault();
    view.classList.add("drop-hot");
  });
  view.addEventListener("dragleave", () => view.classList.remove("drop-hot"));
  view.addEventListener("drop", (event) => void importDropped(event));

  const add = byId<HTMLInputElement>("references-add");
  add.addEventListener("change", async () => {
    await importFiles([...(add.files || [])]);
    add.value = "";
  });

  byId("references-grid").addEventListener("click", (event) => {
    const target = event.target as Element;
    const tile = target.closest<HTMLElement>(".scrap");
    const id = tile?.dataset.id;
    if (!id) return;
    const entry = references().find((item) => item.id === id);
    if (!entry) return;
    if (target.closest(".ref-star")) return flipStar(id);
    if (target.closest(".scrap-index")) return void askChi(id, false);
    if (target.closest(".retry-ref")) return void call(CHANNELS.referencesRetry, id);
    if (target.closest(".toss-ref")) return void call(CHANNELS.referencesRemove, id);
    if (isSaved(entry)) openDetail(entry);
  });

  byId("references-search").addEventListener("input", debounce(() => {
    resetFeed();
    render();
  }, 120));
  byId("references-feed").addEventListener("click", (event) => {
    const target = event.target as Element;
    const tag = target.closest<HTMLElement>(".feed-tag")?.dataset.tag;
    if (tag) {
      pickedTags.add(tag);
      resetFeed();
      render();
      flashToast(t("showing {tag}", { tag: tag.replace(/_/g, " ") }));
      return;
    }
    const id = target.closest<HTMLElement>(".feed-post")?.dataset.id;
    if (id && target.closest(".ref-star")) return flipStar(id);
    const entry = id ? references().find((item) => item.id === id) : null;
    if (entry && isSaved(entry)) openDetail(entry);
  });
  byId("references-layout").addEventListener("click", () => toggleSheet("layout"));
  byId("references-sort").addEventListener("click", () => toggleSheet("sort"));
  byId("references-filter").addEventListener("click", () => toggleSheet("filter"));
  byId("references-reverse").addEventListener("click", () => setPane({ reverse: !pane().reverse }));
  byId("references-sheet").addEventListener("click", (event) => {
    const target = event.target as Element;
    const layout = target.closest<HTMLElement>("[data-layout]")?.dataset.layout;
    const size = target.closest<HTMLElement>("[data-size]")?.dataset.size;
    const sort = target.closest<HTMLElement>("[data-sort]")?.dataset.sort;
    const kind = target.closest<HTMLElement>("[data-kind]")?.dataset.kind;
    const described = target.closest<HTMLElement>("[data-described]")?.dataset.described;
    const favorites = target.closest<HTMLElement>("[data-favorites]")?.dataset.favorites;
    if (layout) {
      setPane({ layout });
      toggleSheet("");
    } else if (size) {
      setPane({ size });
    } else if (sort) {
      pickSort(sort);
      toggleSheet("");
    } else if (kind) {
      setFilter({ kind });
    } else if (described) {
      setFilter({ described });
    } else if (favorites) {
      setFilter({ favorites: favorites === "favorites" });
    }
  });
  // a tap anywhere else, or escape, puts the popover away
  document.addEventListener("click", (event) => {
    if (sheetOpen && !(event.target as Element).closest(".refs-top")) toggleSheet("");
  });
  window.addEventListener("keydown", (event) => {
    if (event.key === "Escape" && sheetOpen) toggleSheet("");
  });
  window.addEventListener("resize", debounce(() => {
    if (isViewActive("references")) render();
  }, 160));
  let feedTick = 0;
  view.addEventListener("scroll", () => {
    if (refMode !== "feed" || feedTick) return;
    feedTick = requestAnimationFrame(() => {
      feedTick = 0;
      feedMaybeMore();
    });
  }, { passive: true });
  onRefFavoritesChanged(() => {
    if (detail().hidden === false && selected) paintStar(byId("reference-star"), selected.id);
  });
  void loadRefFavorites().then(() => render());
  byId("reference-star").addEventListener("click", () => {
    if (selected) flipStar(selected.id);
  });
  void cachedSettings().then((settings) => {
    prefs = settings.refPrefs ? cleanPrefs(settings.refPrefs) : migratedPrefs(settings.refFeedSort);
    render();
  }).catch(() => undefined);
  byId("references-random").addEventListener("click", showRandom);
  byId("references-index").addEventListener("click", async () => {
    try {
      const result = await call<{ pending: number }>(CHANNELS.referencesIndex);
      flashToast(result.pending ? t("chi is tagging {count} refs", { count: result.pending }) : t("every ref is tagged"));
    } catch (error) {
      flashToast(error instanceof Error ? error.message : t("could not start tagging"));
    }
  });
  byId("references-tags").addEventListener("click", (event) => {
    const tag = (event.target as Element).closest<HTMLElement>("[data-tag]")?.dataset.tag;
    if (!tag) return;
    if (pickedTags.has(tag)) pickedTags.delete(tag);
    else pickedTags.add(tag);
    feedLimit = FEED_PAGE;
    byId("view-references").scrollTop = 0;
    render();
  });
  byId("reference-tag-chips").addEventListener("click", async (event) => {
    const tag = (event.target as Element).closest<HTMLElement>("[data-tag]")?.dataset.tag;
    if (!tag) return;
    await closeDetail();
    if (!isViewActive("references")) showView("references", "scrap");
    pickedTags.clear();
    pickedTags.add(tag);
    byId<HTMLInputElement>("references-search").value = "";
    resetFeed();
    render();
    flashToast(t("showing {tag}", { tag: tag.replace(/_/g, " ") }));
  });
  byId("reference-peek").addEventListener("click", () => setInfo(!detail().classList.contains("show-info")));
  byId("reference-next").addEventListener("click", () => showStep(1));
  byId("reference-random").addEventListener("click", showRandom);
  byId("reference-close").addEventListener("click", () => void closeDetail());
  byId("reference-info").addEventListener("click", () => setInfo(!detail().classList.contains("show-info")));
  byId("reference-reindex").addEventListener("click", () => selected && void askChi(selected.id, true));
  byId("reference-open").addEventListener("click", () => selected && call(CHANNELS.openExternal, selected.tweetUrl));
  byId("reference-delete").addEventListener("click", async () => {
    if (!selected || !confirm(t("delete this reference?"))) return;
    await call(CHANNELS.referencesRemove, selected.id);
    hideDetail();
  });
  byId("reference-note").addEventListener("blur", () => void saveCurrent());
  byId("reference-caption").addEventListener("blur", () => void saveCaption());
  // a tap on the dark around the picture closes the lightbox
  byId("reference-stage").addEventListener("click", (event) => {
    const target = event.target as HTMLElement;
    if (dragged) {
      dragged = false;
      return;
    }
    if (target.id === "reference-stage" || target.id === "reference-viewport") void closeDetail();
  });
  byId("reference-tags").addEventListener("blur", () => void saveCurrent());

  window.addEventListener("keydown", (event) => {
    if (detail().hidden) return;
    const typing = event.target instanceof HTMLTextAreaElement || event.target instanceof HTMLInputElement;
    if ((event.ctrlKey || event.metaKey) && event.key === "Enter") {
      event.preventDefault();
      void saveAndNext();
    } else if (event.key === "Escape") {
      event.preventDefault();
      void closeDetail();
    } else if (!typing && event.key === "ArrowRight") {
      showStep(1);
    } else if (!typing && event.key === "ArrowLeft") {
      showBack();
    }
  });
  setupGestures();

  onRefs(() => {
    refreshSelected();
    render();
  });
  listen<{ status?: string; indexing?: boolean; working?: string[] }>(CHANNELS.referencesUpdated, (payload) => {
    if (typeof payload?.indexing === "boolean") indexing = payload.indexing;
    if (Array.isArray(payload?.working)) working = payload.working;
    if (payload?.status === "saved") flashToast(t("ref saved"));
    if (payload?.status === "failed") flashToast(t("ref save failed"));
    if (payload?.indexing === false) flashToast(t("tagging done"));
  });
  listen<LogEntry>(CHANNELS.logsEntry, (entry) => {
    if (entry.level !== "error") return;
    if (entry.source === "references") flashToast(entry.message);
    if (entry.source === "extension" && !entry.message.startsWith("POST /")) flashToast(entry.message);
  });
  onViewShown("references", (pane) => {
    const was = refMode;
    refMode = pane === "manual" ? "manual" : pane === "feed" ? "feed" : "scrap";
    sheetOpen = "";
    // a fresh shuffle each time the feed opens
    if (refMode === "feed" && was !== "feed") {
      feedRank.clear();
      feedLimit = FEED_PAGE;
    }
    byId("view-references").dataset.pane = refMode;
    detail().dataset.pane = refMode;
    render();
    void reload().then(() => {
      if (refMode !== "manual") return;
      const queue = visibleEntries();
      if (queue.length) openDetail(queue[0], true);
    });
  });
}
